/* ============================================================================
   RUB HUB — Mobile banner text auto-fit
   ============================================================================
   v2–v3.3 — overlap-tolerance, spacing-freed-up pass, and their bugfixes.
   v4 — REDESIGN: heading + subheading wrap up to 2 lines with zero overlap
   into the product; blurb runs the container's own full width.
   v5 (2026-08-13, Floody: "I don't want dots") — added shrinkToFitLines():
   wrap first, then shrink the font until the full text fits the 2-line box,
   never truncate. Applied to heading and subheading.

   ★★ v6 (2026-08-23) — HERO-ZONE FIX + VERTICAL BUDGET. Two real defects.

   DEFECT 1 — the product-edge clamp is wrong on hero-zone slides.
   v4/v5 clamp the heading and subheading to the PRODUCT's left edge:

       strictBoundary = Math.min(productLeft, containerRightEdge)

   That was correct for the pre-hero-zone mobile layout, where the text sat
   BESIDE the product. Under hero-zone the product sits in its own window in
   the UPPER part of the canvas and the text stack sits BELOW it — there is no
   horizontal conflict at all. The clamp was still firing, cutting the heading
   to the product's left edge: 245px instead of the available 341px on a 390px
   phone, a 31% width cut, manufacturing two-line headings that had no reason
   to wrap. Measured on Blues Hog: 6 of 16 slides.
   v6 only applies the product clamp on NON-hero-zone slides.

   DEFECT 2 — nothing ever checked the VERTICAL budget.
   v5 fits each tier to its own line count and stops. It has no idea where the
   button row is, so a 2-line heading plus a 2-line tagline plus a 3-line blurb
   would happily run straight through the CTA. Combined with the old CSS
   (which bottom-anchored the block and clipped it with overflow:hidden), copy
   silently disappeared. v6 adds fitBlockHeight(): after the per-tier pass, if
   the block still overflows its now-bounded box, all three tiers are scaled
   down together by ONE shared multiplier until it fits — the same shared-scale
   approach the desktop system already uses, so the type hierarchy is preserved
   at every size rather than shrinking the headline into the tagline.

   Floody's spec this implements, verbatim: "the headline can be two lines, but
   needs to shrink to fit. The tagline can be two lines, but also needs to
   shrink to fit. And the blurb is written at three lines is fine."

   PAIRS WITH: the section's mobile hero-zone CSS, which now gives the block a
   real top+bottom bounded box, and rh-mobile-banner-overlay-master.css v2.0,
   which scopes its legacy overrides away from hero-zone slides. This script
   cannot do its job without that bounded box — clientHeight would be unbounded
   and fitBlockHeight would never fire.
   ============================================================================ */
(function () {
  var MOBILE_BREAKPOINT = 749;
  var HEADING_MIN_FONT_PX = 16;
  var SUBHEADING_MIN_FONT_PX = 9;
  var BLURB_MIN_FONT_PX = 8;

  /* Floody's spec: headline up to 2 lines, tagline up to 2 lines, blurb 3.
     These MUST match the -webkit-line-clamp values in
     assets/rh-mobile-banner-overlay-master.css §5. */
  var HEADING_MAX_LINES = 2;
  var SUBHEADING_MAX_LINES = 2;

  var BLOCK_SCALE_STEP = 0.03;   // 3% per pass
  var BLOCK_SCALE_FLOOR = 0.55;  // never below 55% of natural
  var GUARD = 40;

  function setMaxWidthToBoundary(el, textLeft, boundary, minUsablePx) {
    if (!el) return;
    var available = boundary - textLeft;
    if (available > minUsablePx) {
      el.style.maxWidth = available + 'px';
    } else {
      el.style.maxWidth = '';
    }
  }

  /* ---- v6.2 (2026-08-23) — OVERFLOW DETECTION REWRITTEN. ----------------
     v5 and v6.0/6.1 both tested "is this clamped element cutting content?"
     with `el.scrollHeight > el.clientHeight + 1`. That test is BROKEN on a
     `display:-webkit-box` + `-webkit-line-clamp` element: its scrollHeight
     reports ~2px MORE than clientHeight permanently, even when the text is a
     single line inside a 2-line clamp and nothing is being cut. Measured on
     Blues Hog at 390px: every one of the 16 headings reported "overflowing"
     while rendering in full.

     The consequence was severe and silent — both loops could never satisfy
     their exit condition, so they shrank all the way to their floors. The
     headings ended up at 15.15px against a natural 33px (a 54% reduction, at
     the 16px floor), which is why the mobile headlines looked so small. This
     is almost certainly the same flaw behind the long-standing ellipsis
     report in backlog item #1.

     Both tests are now GEOMETRIC and artifact-proof:
       · overflowsLines() momentarily lifts the clamp, measures the text's
         true natural height, restores the clamp, and compares against
         lineHeight x maxLines. Exact, no tolerance fudging.
       · blockOverflows() compares the last visible tier's real bottom edge
         against the block's real bottom edge. No scrollHeight involved.
     ---------------------------------------------------------------------- */

  function overflowsLines(el, maxLines) {
    var cs = getComputedStyle(el);
    var lh = parseFloat(cs.lineHeight);
    if (!lh || isNaN(lh)) lh = parseFloat(cs.fontSize) * 1.2;
    var prevDisplay = el.style.display;
    var prevClamp = el.style.webkitLineClamp;
    el.style.display = 'block';
    el.style.webkitLineClamp = 'unset';
    var natural = el.scrollHeight;
    el.style.display = prevDisplay;
    el.style.webkitLineClamp = prevClamp;
    return natural > (lh * maxLines) + 1;
  }

  function blockOverflows(textBox, tiers) {
    var boxBottom = textBox.getBoundingClientRect().bottom;
    var last = null;
    tiers.forEach(function (t) {
      if (t.el && t.el.getBoundingClientRect().height > 0) last = t.el;
    });
    if (!last) return false;
    return last.getBoundingClientRect().bottom > boxBottom + 0.5;
  }

  /* Wrap-then-shrink, per tier: let it wrap up to maxLines, and only if the
     full text still will not fit in that many lines, step the font down. */
  function shrinkToFitLines(el, minFontPx, maxLines) {
    if (!el) return;
    el.style.fontSize = '';
    var natural = parseFloat(getComputedStyle(el).fontSize);
    if (!natural) return;
    var size = natural;
    var guard = 0;
    while (size > minFontPx && guard < 60) {
      if (!overflowsLines(el, maxLines)) break;
      size -= 1;
      el.style.fontSize = size + 'px';
      guard++;
    }
  }

  /* Whole-block vertical fit. Scales every tier by one shared multiplier so
     the hierarchy holds, until the stack fits the block's bounded height. */
  function fitBlockHeight(textBox, tiers) {
    if (!textBox || !blockOverflows(textBox, tiers)) return;

    var naturals = tiers.map(function (t) {
      return t.el ? parseFloat(getComputedStyle(t.el).fontSize) : 0;
    });

    var scale = 1;
    var guard = 0;
    while (guard < GUARD) {
      if (!blockOverflows(textBox, tiers)) break;
      scale -= BLOCK_SCALE_STEP;
      if (scale < BLOCK_SCALE_FLOOR) break;

      var moved = false;
      tiers.forEach(function (t, i) {
        if (!t.el || !naturals[i]) return;
        var current = parseFloat(t.el.style.fontSize) || naturals[i];
        var next = Math.max(t.min, naturals[i] * scale);
        if (Math.abs(next - current) > 0.01) moved = true;
        t.el.style.fontSize = next + 'px';
      });
      if (!moved) break;
      guard++;
    }
  }

  function fitSlide(slide) {
    if (window.innerWidth > MOBILE_BREAKPOINT) return;
    var textBox = slide.querySelector('.rh-cbb__slidetext');
    if (!textBox) return;

    var isHeroZone = slide.classList.contains('rh-cbb__slide--hz');

    var heading = slide.querySelector('.rh-cbb__slidetext-heading');
    var subheading = slide.querySelector('.rh-cbb__slidetext-subheading');
    var blurb = slide.querySelector('.rh-cbb__slidetext-blurb--mobile');

    // Reset every tier so repeated runs (resize, slide change, font load)
    // measure from natural size instead of compounding previous shrinks.
    [heading, subheading, blurb].forEach(function (el) {
      if (el) { el.style.fontSize = ''; el.style.maxWidth = ''; }
    });

    var textBoxRect = textBox.getBoundingClientRect();
    var textLeft = textBoxRect.left;
    var containerRightEdge = textBoxRect.right;

    /* v6: the product only constrains text width on the LEGACY layout, where
       the two sit side by side. On a hero-zone slide the text is below the
       product and gets the container's full width. */
    var boundary = containerRightEdge;
    if (!isHeroZone) {
      var overlay = slide.querySelector('.rh-cbb__product-overlay');
      if (overlay) {
        var overlayRect = overlay.getBoundingClientRect();
        if (overlayRect.width > 0) {
          boundary = Math.min(overlayRect.left, containerRightEdge);
        }
      }
    }

    setMaxWidthToBoundary(heading, textLeft, boundary, 60);
    shrinkToFitLines(heading, HEADING_MIN_FONT_PX, HEADING_MAX_LINES);

    setMaxWidthToBoundary(subheading, textLeft, boundary, 60);
    shrinkToFitLines(subheading, SUBHEADING_MIN_FONT_PX, SUBHEADING_MAX_LINES);

    if (blurb) {
      setMaxWidthToBoundary(blurb, textLeft, containerRightEdge, 60);
    }

    // v6: finally, make the whole stack fit the block's bounded height.
    fitBlockHeight(textBox, [
      { el: heading,    min: HEADING_MIN_FONT_PX },
      { el: subheading, min: SUBHEADING_MIN_FONT_PX },
      { el: blurb,      min: BLURB_MIN_FONT_PX }
    ]);
  }

  /* v6.1 (2026-08-23) — fit EVERY slide, not just the active one.
     v5 and earlier only ever processed '.rh-cbb__slide.is-active'. On a
     carousel that is technically enough (the MutationObserver re-fits each
     slide as it becomes active), but it means a slide's first painted frame
     after advancing shows UNFITTED text — which, now that the block is a
     bounded box with overflow:hidden, reads as a visible clip. Measured on
     Blues Hog at 390px: 2 of 16 slides were clipped at any given moment
     purely because they had not been fitted yet; fitting all 16 dropped it
     to 0. The work is trivial DOM math and slides keep their layout width
     while off-screen (the track translates, it does not display:none), so
     measuring them is safe. */
  function fitAll() {
    document.querySelectorAll('.rh-cbb__slide').forEach(function (slide) {
      if (slide.getBoundingClientRect().width < 10) return;  // not laid out yet
      fitSlide(slide);
    });
  }

  function wireImageLoad() {
    document.querySelectorAll('.rh-cbb__product-overlay').forEach(function (img) {
      if (img.hasAttribute('data-rh-autofit-bound')) return;
      img.setAttribute('data-rh-autofit-bound', 'true');
      if (img.complete && img.naturalWidth > 0) return;
      img.addEventListener('load', function () {
        var slide = img.closest('.rh-cbb__slide');
        if (slide) fitSlide(slide);
      });
    });
  }

  function init() {
    fitAll();
    wireImageLoad();
    window.addEventListener('resize', fitAll);
    window.addEventListener('orientationchange', fitAll);
    document.querySelectorAll('.rh-cbb__slider').forEach(function (slider) {
      var obs = new MutationObserver(function () {
        fitAll();
        wireImageLoad();
      });
      obs.observe(slider, { attributes: true, attributeFilter: ['class'], subtree: true });
    });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fitAll);
    }
    setTimeout(fitAll, 150);
    setTimeout(fitAll, 600);
  }

  // Exposed so a QA pass can force a re-fit after simulating a phone width.
  window.rhMobileSlidetextFit = fitAll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
